import React, {ReactPropTypes, useEffect, useMemo, useState} from 'react';
import ScrollingFeaturedCard, {
  IScrollingFeaturedCard,
} from '@wearesection/bandai-react-components/organisms/ScrollingFeaturedCard';
import * as Types from 'graphql/types';
import {ComponentPropsAdapter} from '../../types';
import RenderNextImage from '../../atoms/render-next-image';
import {PLACEHOLDER_IMG} from '~/constants';
import {
  convertPlatformToIcon,
  convertToSlug,
  handleCovertTagPrimary,
  handleFilterTags,
  handleReturnIDVideo,
  handleReturnImg,
  handleReturnSubtitleLanguage,
  handleReturnTypeVideo,
} from '~/utils';
import {GetRouter} from '~/utils';
import {Box} from '@mui/material';
import {handleReturnDateForLocal} from '../scrolling-thumbnail-card/scrolling-thumbnail-card';
import {handleReturnLocaleStore} from '../home-page-mash-thead/home-page-mash-thead';

type FeaturedCardCarouselExt = Types.NewsListing & Types.GamesListing;

const adapterNewsListing: ComponentPropsAdapter<
  Types.NewsListing & any,
  IScrollingFeaturedCard
> = data => {
  const {pathname, locale} = GetRouter();
  return {
    isNewPage: true,
    title: data?.dataTranslation?.['title-our-pick'] ?? '',
    listFeaturedNewsCard:
      data?.listItemCollection?.items?.map((item: any) => {
        return {
          title: item?.title ?? '',
          description: item?.shortDescription ?? '',
          linkHref: `/${locale}${pathname}/${item?.slug}` ?? '',
          mainImage:
            item?.mainImage?.media?.url ||
            item?.mastheadBackgroundImage?.media?.url ||
            PLACEHOLDER_IMG,
          hrefTag: `/${locale}${pathname}`,
          mainImageNext: (
            <RenderNextImage
              src={
                item?.mainImage?.media?.url ||
                item?.mastheadBackgroundImage?.media?.url ||
                PLACEHOLDER_IMG
              }
              width={511}
              height={488}
              alt={item?.title || ''}
            />
          ),
          tags: [
            {
              title: handleCovertTagPrimary(
                (item?.customTags as string)?.toUpperCase(),
                item?.game?.releaseDate ?? '',
                locale as string
              ),
              color: (data as any)?.listItemCollection?.listTags?.find(
                (e: any) =>
                  e?.name?.toLocaleLowerCase() ===
                  (handleCovertTagPrimary(
                    (item?.customTags as string)?.toUpperCase(),
                    item?.game?.releaseDate ?? '',
                    locale as string
                  )?.toLocaleLowerCase() as string)
              )?.backgroundColor,
            },
          ],

          date: handleReturnDateForLocal(
            locale || 'en-sg',
            `${item?.publishDate}`.substring(0, 10)
          ),
          // variant: 'horizontal',
        };
      }) ?? [],
  };
};

const adapterGamesListing: ComponentPropsAdapter<
  Types.GamesListing & {
    locale?: string;
    useRouter1: ReactPropTypes;
    dataTranslation: any;
  },
  IScrollingFeaturedCard
> = data => {
  const {pathname, locale} = GetRouter();
  const isLocalDisableBtnBuy = false;
  // ['ko-kr'].includes((locale as string)?.toLowerCase());

  return {
    title: data?.dataTranslation?.['title-our-pick'] ?? '',
    listFeaturedNewsCard:
      data?.listItemCollection?.items?.map((item:any) => {
        const BCNameGame = (item?.bcProductReference as any)?.site?.product?.name || '';
        return {
          title: item?.title ?? '',
          description: item?.description ?? '',
          linkHref: `/${locale}${pathname}/${item?.slug}` ?? '',
          mainImage: item?.mainThumbnailImg?.media?.url ?? PLACEHOLDER_IMG,
          hrefTag: `/${locale}${pathname}`,
          mainImageNext: (
            <RenderNextImage
              src={item?.mainThumbnailImg?.media?.url ?? PLACEHOLDER_IMG}
              width={511}
              height={488}
              alt={item?.title || ''}
            />
          ),
          tags:
            handleFilterTags(item?.platform as [])?.map((item: any) => ({
              color: '#737373',
              title: React.createElement(convertPlatformToIcon(item)),
              disabled: false,
            })) || [],
          variant: 'horizontal',
          language: handleReturnSubtitleLanguage(item?.subtitle || [], locale),
          button:
            [
              isLocalDisableBtnBuy
                ? null
                : BCNameGame
                ? {
                    label: data?.dataTranslation?.['game-listing-buy'] || '',
                    externalLink: isLocalDisableBtnBuy
                      ? ''
                      : `${process.env.NEXT_PUBLIC_STORE_SITE_URL}${handleReturnLocaleStore(
                          locale || ''
                        )}/store/games/${convertToSlug(BCNameGame)}`,
                    target: '_parent',
                    variant: 'primaryDark',
                  }
                : null,
              item?.mastheadGalleryCollection?.items?.length
                ? {
                    label: data?.dataTranslation?.['game-detailed-watch-trailer'],
                    target: '/',
                    variant: 'secondary',
                  }
                : {},
            ] ?? [],
          trailers:
            item?.mastheadGalleryCollection?.items?.map((item:any) => {
              return {
                trailerUrl: handleReturnIDVideo(item?.videoEmbed as string) ?? '',
                trailerTitle: item?.caption ?? '',
                trailerImg: handleReturnImg(item?.videoEmbed as string, item) || PLACEHOLDER_IMG,
                videoType: handleReturnTypeVideo(item?.videoEmbed as string) ?? '',
              };
            }) || [],
          sku: item?.bcProductReference,
          ctaLearnMore:{
            label: data?.dataTranslation['cta-learn-more'] ?? "Learn more",
            url: `/${locale}${pathname}/${item?.slug}` || '',
            target: null,
          }
        };
      }) ?? [],
  };
};

const handleTypeName = (props: any) => {
  const typeName: any = {
    NewsListing: adapterNewsListing(props),
    GamesListing: adapterGamesListing(props),
  };
  return typeName[props.__typename] || typeName['NewsListing'];
};

const PADDING_COMPONENT: any = {
  '/games': {
    paddingTop: {
      xs: '28px',
      lg: '40px',
    },
    paddingBottom: {
      xs: '72px',
      lg: '40px',
    },
  },
  '/news': {
    paddingBottom: {
      xs: '24px',
      lg: '40px',
    },
  },
};

const ScrollingFeaturedCardOrganisms: React.FC<FeaturedCardCarouselExt> = (props: any) => {
  const {pathname} = GetRouter();

  const ComponentProps = handleTypeName(props);

  return (
    <Box sx={PADDING_COMPONENT[pathname] ?? {}}>
      <ScrollingFeaturedCard {...ComponentProps} />
    </Box>
  );
};

export default ScrollingFeaturedCardOrganisms;
